import { _isNil, _notNil } from '@/littledash';
import type { SendBundleValidationResult } from '@/model/Agent.model';
import type { FC } from 'react';
import { sendDomainLabel } from '@/components/Agents/SendValidator/utils/sendDomains';
import SendOmissionNotice from './SendOmissionNotice';
import {
  AI_REVIEW_NOT_RECORDED_TEXT,
  aiReviewOutcomeOf,
  aiUseLabelOf,
  IN_VIVO_AI,
} from '@/components/Agents/SendValidator/utils/sendAiUse';
import { omissionOf } from '@/components/Agents/SendValidator/utils/sendOmission';
import { exactPassOf, runVerdictText } from '@/components/Agents/SendValidator/utils/sendStoredReport';
import { fileKeysOf, plural } from '@/components/Agents/SendValidator/utils/sendText';
import styles from './sendResults.module.scss';

// Chip's blue and gray schemes, as the source badge draws them, so the label sits at the same weight.
const aiUseColour = {
  without_ai: 'blue b--lightest-blue bg-lightest-blue',
  review_failed: 'near-black b--orange bg-white',
  with_ai: 'near-black b--moon-gray bg-moon-gray',
  not_recorded: 'dark-gray b--moon-gray bg-white',
} as const;

interface SendRunHeaderProps {
  result: SendBundleValidationResult;
  testId?: string;
}

/**
 * The top of a SEND report: how many files were checked and each one's rows, whether Benchling In
 * Vivo AI took part, whether the run passed validation exactly as the server decided it (and which part
 * stopped it), and what the whole report left out. Rows are given per file as the server
 * counted them, never summed here. A result that recorded no run metadata says AI use was not
 * recorded rather than guessing either way.
 */
const SendRunHeader: FC<SendRunHeaderProps> = ({ result, testId = 'invivo-send-validator__run-header' }) => {
  const aiUse = aiUseLabelOf(result.run_metadata);
  const aiUseKey = _isNil(aiUse) ? 'not_recorded' : aiUse.key;
  const fileKeys = fileKeysOf(result.per_domain);
  const review = aiReviewOutcomeOf(result.run_metadata);
  // An out-of-scope run checks nothing, so its files are only uploaded, never presented as checked.
  const notChecked = result.scope?.state === 'out_of_scope';
  const aiMayHaveRun = result.run_metadata?.mode !== 'model_free' && _notNil(result.run_metadata);
  // Findings only: AI notes left out are announced in their own section.
  const omission = { ...omissionOf(result.report.summary, result.report.advisory_counts), aiNotesNotShown: 0 };
  return (
    <div className="ui-card pa4" data-test-component="SendRunHeader" data-testid={testId}>
      <div className="flex items-center flex-wrap">
        <h2 className={`${styles.sectionTitle} mr3`} data-test-element="file-count">
          {plural(result.per_domain.length, 'file', 'files')} {notChecked ? 'uploaded' : 'checked'}
        </h2>
        <span
          className={`f7 lh-title dib br2 ba ph2 pv1 ${styles.nowrap} ${aiUseColour[aiUseKey]}`}
          data-test-element="ai-use"
          data-test-key={aiUseKey}
        >
          {aiUse?.text ?? `${IN_VIVO_AI} use not recorded`}
        </span>
      </div>
      <p className="f6 near-black ma0 mt2" data-test-element="run-verdict">
        {runVerdictText(exactPassOf(result.report, result.scope), result.scope)}
      </p>
      {review === 'not_recorded' && aiMayHaveRun && (
        <p className="f6 dark-gray ma0 mt2" data-test-element="ai-review" data-test-key="not_recorded">
          {AI_REVIEW_NOT_RECORDED_TEXT}
        </p>
      )}
      <ul className={styles.runFiles} data-test-element="files">
        {result.per_domain.map((file, index) => (
          <li
            key={fileKeys[index]}
            className={styles.runFile}
            data-test-element="file"
            data-test-key={file.domain_code}
          >
            <span className="fw6 near-black">{sendDomainLabel(file.domain_code)}</span>{' '}
            <span className="dark-gray">· {plural(file.row_count, 'row', 'rows')}</span>
          </li>
        ))}
      </ul>
      <SendOmissionNotice omission={omission} testId="invivo-send-validator__report-omission" />
    </div>
  );
};

export default SendRunHeader;
